<script setup lang="ts">
import { computed, ref, watch, nextTick, defineAsyncComponent, onBeforeUnmount, onUnmounted } from 'vue'
import { useTimeoutFn } from '@vueuse/core'
import { useMousePreview } from '../../composables/useMousePreview'
import { useDrag } from '../../composables/useDrag'
import { useStrapiStore } from '../../stores/strapi'
import { usePanelAnimations } from '../../composables/usePanelAnimations'
import SidePanel from './SidePanel.vue'

// Lazy load panel components to reduce initial bundle size
const PanelDesign = defineAsyncComponent(() => import('../../views/PanelDesign.vue'))
const PanelTech = defineAsyncComponent(() => import('../../views/PanelTech.vue'))

interface Props {
  active: 'left' | 'right' | null
  isRouteTransitioning: boolean
}

interface Emits {
  (e: 'open', side: 'left' | 'right'): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const strapiStore = useStrapiStore()
const { showLeftPreview, showRightPreview, leftPanelPosition, rightPanelPosition } = useMousePreview()
const { isDragging } = useDrag()

// Animation coordinator for managing panel transitions
const { switchPanels, isAnyAnimationRunning } = usePanelAnimations()

// Panel element references for coordinated animations
const leftPanelRef = ref<InstanceType<typeof SidePanel> | null>(null)
const rightPanelRef = ref<InstanceType<typeof SidePanel> | null>(null)

// Track previous active panel for switching animations
const previousActive = ref<'left' | 'right' | null>(null)

// Memory management - VueUse timeout functions for automatic cleanup
const { start: scheduleLeftCleanup, stop: cancelLeftCleanup } = useTimeoutFn(() => {
  if (!isUnmounted) {
    leftContentRendered.value = false
    console.debug('Left panel content cleaned up')
  }
}, 30000, { immediate: false })

const { start: scheduleRightCleanup, stop: cancelRightCleanup } = useTimeoutFn(() => {
  if (!isUnmounted) {
    rightContentRendered.value = false
    console.debug('Right panel content cleaned up')
  }
}, 30000, { immediate: false })

// Track which panels should have content rendered
const leftContentRendered = ref(false)
const rightContentRendered = ref(false)

// Cleanup management
let isUnmounted = false

// Memory management helper functions
const markContentAccessed = (side: 'left' | 'right') => {
  // Cancel any pending cleanup and mark as accessed
  if (side === 'left') {
    cancelLeftCleanup()
  } else {
    cancelRightCleanup()
  }
  console.debug(`Content accessed: ${side}`)
}

const scheduleContentCleanup = (side: 'left' | 'right') => {
  // Schedule cleanup after 30 seconds using VueUse
  if (side === 'left') {
    scheduleLeftCleanup()
  } else {
    scheduleRightCleanup()
  }
}

const cancelContentCleanup = (side: 'left' | 'right') => {
  // Cancel scheduled cleanup
  if (side === 'left') {
    cancelLeftCleanup()
  } else {
    cancelRightCleanup()
  }
}

// Computed properties for content rendering decisions
const shouldRenderLeftContent = computed(() => {
  if (isUnmounted) return false
  return props.active === 'left' || 
         showLeftPreview.value || 
         leftContentRendered.value
})

const shouldRenderRightContent = computed(() => {
  if (isUnmounted) return false
  return props.active === 'right' || 
         showRightPreview.value || 
         rightContentRendered.value
})

const designTitle = computed(() => strapiStore.designContent?.title || 'Design')
const technologyTitle = computed(() => strapiStore.technologyContent?.title || 'Technology')

const handleDragOpen = async (side: 'left' | 'right') => {
  if (isUnmounted) return
  
  if (isAnyAnimationRunning()) return
  
  if (props.active && props.active !== side) {
    const closingPanel = props.active === 'left' ? leftPanelRef.value : rightPanelRef.value
    const openingPanel = side === 'left' ? leftPanelRef.value : rightPanelRef.value
    
    if (closingPanel?.$el && openingPanel?.$el) {
      // Use staggered panel switching animation
      await switchPanels(
        closingPanel.$el,
        openingPanel.$el,
        props.active,
        side
      )
    }
  }
  
  // Cancel any pending cleanup for this panel
  cancelContentCleanup(side)
  
  // Mark content as accessed for memory management
  markContentAccessed(side)
  
  // Ensure content is rendered before opening
  if (side === 'left') {
    leftContentRendered.value = true
  } else {
    rightContentRendered.value = true
  }
  
  // Wait for content to be rendered
  try {
    await nextTick()
    if (!isUnmounted) {
      emit('open', side)
    }
  } catch (error) {
    console.warn('Panel open error:', error)
  }
}

const handleClose = () => {
  if (isUnmounted || isAnyAnimationRunning()) return
  
  const closedSide = props.active
  emit('close')
  
  // Schedule content cleanup after a delay (allows for quick reopening)
  if (closedSide) {
    scheduleContentCleanup(closedSide)
  }
}

// Complete cleanup function
const cleanup = () => {
  isUnmounted = true
  
  // Cancel any pending content cleanups - VueUse handles this automatically
  cancelContentCleanup('left')
  cancelContentCleanup('right')
  
  // Reset content rendering flags
  leftContentRendered.value = false
  rightContentRendered.value = false
}

// Track active panel changes for coordinated animations
watch(() => props.active, (newActive, oldActive) => {
  previousActive.value = oldActive || null
}, { immediate: true })

// VueUse automatically handles cleanup for watchers
watch([showLeftPreview, showRightPreview], ([leftPreview, rightPreview]) => {
  if (isUnmounted) return
  
  if (leftPreview && !leftContentRendered.value) {
    leftContentRendered.value = true
    markContentAccessed('left')
  }
  if (rightPreview && !rightContentRendered.value) {
    rightContentRendered.value = true
    markContentAccessed('right')
  }
}, { flush: 'post' })

// Clean up when component unmounts
onBeforeUnmount(cleanup)
onUnmounted(cleanup)
</script>

<template>
  <div class="side-panels">
    <SidePanel 
      ref="leftPanelRef"
      side="left" 
      :open="props.active === 'left'"
      :preview="showLeftPreview && !isDragging && !props.active"
      :dynamic-position="leftPanelPosition"
      :is-route-transitioning="props.isRouteTransitioning"
      :title="designTitle"
      @dragOpen="handleDragOpen('left')"
      @close="handleClose"
    >
      <Suspense v-if="shouldRenderLeftContent">
        <PanelDesign />
        <template #fallback>
          <div class="panel-loading">Loading...</div>
        </template>
      </Suspense>
    </SidePanel>

    <SidePanel 
      ref="rightPanelRef"
      side="right" 
      :open="props.active === 'right'"
      :preview="showRightPreview && !isDragging && !props.active"
      :dynamic-position="rightPanelPosition"
      :is-route-transitioning="props.isRouteTransitioning"
      :title="technologyTitle"
      @dragOpen="handleDragOpen('right')"
      @close="handleClose"
    >
      <Suspense v-if="shouldRenderRightContent">
        <PanelTech />
        <template #fallback>
          <div class="panel-loading">Loading...</div>
        </template>
      </Suspense>
    </SidePanel>
  </div>
</template>

<style scoped>
.panel-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--theme-text);
  font-size: 16px;
}
</style>
